import React, { useState } from 'react';
import { X, Building, User, Hash, Loader2, Banknote } from 'lucide-react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  onSuccess: (newBalance: number) => void;
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({ isOpen, onClose, currentBalance, onSuccess }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('Telebirr');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (numAmount > currentBalance) {
      return alert(t('withdraw_limit_err', "You cannot withdraw more than your current balance."));
    }
    if (numAmount < 50) {
      return alert(t('withdraw_min_err', "Minimum withdrawal amount is 50 ETB."));
    }

    try {
      setIsSubmitting(true);
      
      const res = await api.post('/payments/withdraw', {
        amount: numAmount,
        bankName,
        accountName,
        accountNumber
      });
      
      alert(t('payout_requested_success', "✅ Payout requested successfully! It is now pending admin review."));
      onSuccess(res.data.newBalance);
      onClose();
    } catch (err: any) {
      alert(err.response?.data?.message || t('failed_submit_request', "Failed to submit request"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-950/50">
          <h2 className="text-white font-black uppercase tracking-widest flex items-center gap-2">
            <Banknote className="text-blue-500" size={18} /> {t('request_payout', 'Request Payout')}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-2xl mb-6 text-center">
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">{t('available_balance', 'Available Balance')}</p>
            <p className="text-2xl font-black text-blue-400">{currentBalance.toFixed(2)} <span className="text-sm">ETB</span></p>
          </div>

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{t('withdrawal_amount', 'Withdrawal Amount (ETB)')}</label>
            <input 
              type="number" 
              required
              min="50"
              max={currentBalance}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full mt-1 bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-bold"
              placeholder={t('eg_amount', 'e.g. 500')}
            />
          </div>

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{t('transfer_method', 'Transfer Method')}</label>
            <div className="relative mt-1">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <select 
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-bold appearance-none"
              >
                <option value="Telebirr">Telebirr</option>
                <option value="CBE">CBE (Commercial Bank of Ethiopia)</option>
                <option value="Awash Bank">Awash Bank</option>
                <option value="Dashen Bank">Dashen Bank</option>
                <option value="Abyssinia Bank">Abyssinia Bank</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{t('account_holder_name', 'Account Holder Name')}</label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input 
                type="text" 
                required
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-bold"
                placeholder={t('name_on_account', 'Name on the account')}
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{t('account_phone_number', 'Account / Phone Number')}</label>
            <div className="relative mt-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input 
                type="text" 
                required
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-bold"
                placeholder={t('account_number_placeholder', 'Account number')}
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting || !amount || parseFloat(amount) > currentBalance}
            className="w-full mt-4 bg-blue-600 text-white py-3.5 rounded-xl font-black uppercase tracking-widest shadow-lg hover:bg-blue-500 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : t('request_transfer', 'Request Transfer')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WithdrawModal;